
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import TopNavigation from '../../../components/feature/TopNavigation';
import Button from '../../../components/base/Button';

export default function TripTrackingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [tripStatus, setTripStatus] = useState<'onTheWay' | 'arrived' | 'inProgress' | 'completed'>('onTheWay');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState('');
  
  const tripData = location.state || {
    pickup: 'الرياض، حي الملز',
    destination: 'الرياض، حي النخيل',
    price: 15,
    driver: {
      name: 'أحمد محمد',
      rating: 4.8,
      phone: '+966501234567',
      carModel: 'تويوتا كامري 2022',
      plateNumber: 'أ ب ج 1234'
    }
  };

  useEffect(() => {
    // Simulate trip progress
    const timer1 = setTimeout(() => setTripStatus('arrived'), 5000);
    const timer2 = setTimeout(() => setTripStatus('inProgress'), 10000);
    const timer3 = setTimeout(() => setTripStatus('completed'), 15000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  useEffect(() => {
    if (tripStatus === 'completed') {
      setTimeout(() => {
        navigate('/trip/payment', { state: tripData });
      }, 2000);
    }
  }, [tripStatus, navigate, tripData]);

  const handleCall = () => {
    window.location.href = `tel:${tripData.driver.phone}`;
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      // Simulate sending message
      setMessage('');
      setShowMessageModal(false);
      // Show success feedback
      alert(t('messageSent'));
    }
  };

  const getStatusInfo = () => {
    switch (tripStatus) {
      case 'onTheWay':
        return {
          title: t('driverOnWay'),
          subtitle: 'السائق في طريقه إليك',
          icon: 'ri-car-line',
          color: 'text-blue-600',
          bgColor: 'bg-blue-100'
        };
      case 'arrived':
        return {
          title: t('arrived'),
          subtitle: 'وصل السائق إلى موقعك',
          icon: 'ri-map-pin-line',
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
      case 'inProgress':
        return {
          title: t('tripInProgress'),
          subtitle: 'الرحلة جارية إلى الوجهة',
          icon: 'ri-navigation-line',
          color: 'text-orange-600',
          bgColor: 'bg-orange-100'
        };
      case 'completed':
        return {
          title: t('tripCompleted'),
          subtitle: 'وصلت إلى وجهتك بأمان',
          icon: 'ri-check-line',
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-20">
      <TopNavigation title={t('trackTrip')} />
      
      <div className="pt-20 px-4">
        {/* Status Card */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <div className="flex items-center mb-4">
            <div className={`w-12 h-12 ${statusInfo.bgColor} rounded-full flex items-center justify-center mr-4`}>
              <i className={`${statusInfo.icon} ${statusInfo.color} text-xl`}></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">{statusInfo.title}</h3>
              <p className="text-sm text-gray-600">{statusInfo.subtitle}</p>
            </div>
          </div>
          
          {tripStatus === 'onTheWay' && (
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-blue-800 text-sm font-medium">الوقت المتوقع للوصول: 5 دقائق</p>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="bg-white rounded-2xl mb-6 overflow-hidden shadow-sm border border-gray-100">
          <div className="h-64 bg-gray-100 relative">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.1!2d46.6753!3d24.7136!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQyJzQ5LjAiTiA0NsKwNDAnMzEuMSJF!5e0!3m2!1sen!2ssa!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
            
            {/* Driver Location */}
            <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-8 h-8 bg-[#0056A6] rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                <i className="ri-car-fill text-white text-sm"></i>
              </div>
            </div>
            
            {/* Destination */}
            <div className="absolute bottom-1/3 right-1/3 transform translate-x-1/2 translate-y-1/2">
              <div className="w-6 h-6 bg-[#F9A826] rounded-full border-4 border-white shadow-lg"></div>
            </div>
          </div>
        </div>

        {/* Driver Info */}
        <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-fill text-gray-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-800">{tripData.driver.name}</h4>
                <div className="flex items-center">
                  <i className="ri-star-fill text-yellow-400 text-sm mr-1"></i>
                  <span className="text-sm text-gray-600">{tripData.driver.rating}</span>
                </div>
              </div>
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600">{tripData.driver.carModel}</p>
              <p className="text-sm font-bold text-gray-800">{tripData.driver.plateNumber}</p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              onClick={handleCall}
              className="flex-1"
              variant="outline"
            >
              <i className="ri-phone-line mr-2"></i>
              {t('callDriver')}
            </Button>
            <Button
              onClick={() => setShowMessageModal(true)}
              className="flex-1"
              variant="outline"
            >
              <i className="ri-message-line mr-2"></i>
              {t('messageDriver')}
            </Button>
          </div>
        </div>

        {/* Trip Details */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">تفاصيل الرحلة</h3>
          
          <div className="space-y-3">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-[#0056A6] rounded-full mr-3"></div>
              <p className="text-sm text-gray-700 flex-1">{tripData.pickup}</p>
            </div>
            <div className="border-r border-gray-200 mr-1.5 h-4"></div>
            <div className="flex items-center">
              <div className="w-3 h-3 border-2 border-[#F9A826] rounded-full mr-3"></div>
              <p className="text-sm text-gray-700 flex-1">{tripData.destination}</p>
            </div>
          </div>
          
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-600">السعر</span>
            <span className="text-lg font-bold text-[#0056A6]">{tripData.price} ريال</span>
          </div>
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">{t('messageTitle')}</h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-600"></i>
              </button>
            </div>
            
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('typeMessage')}
              className="w-full h-24 p-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:border-[#0056A6] mb-4"
              maxLength={500}
            />
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowMessageModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium"
              >
                {t('cancel')}
              </button>
              <Button
                onClick={handleSendMessage}
                className="flex-1"
                disabled={!message.trim()}
              >
                {t('send')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
